/**
 * The attack, combo and chain tables from `docs/DESIGN.md`, as data.
 *
 * Nothing here is derived or computed: the spec gives literals, and these are
 * those literals. The lookups only clamp.
 */

/**
 * Attack for a plain clear, indexed by rows cleared.
 *
 * `docs/DESIGN.md`: single 0, double 1, triple 2, quad 4.
 */
export const LINE_ATTACK: readonly number[] = [0, 0, 1, 2, 4];

/**
 * Attack for a T-spin clear, indexed by rows cleared.
 *
 * `docs/DESIGN.md`: T-spin single 2, double 4, triple 6. Index 0 is a spin that
 * cleared nothing and index 4 cannot happen — a T covers three rows at most —
 * but the row is there so the lookup never falls off the end.
 */
export const TSPIN_ATTACK: readonly number[] = [0, 2, 4, 6, 6];

/**
 * Combo bonus, indexed by combo count, clamped at 12.
 *
 * `docs/DESIGN.md`: `0, 1, 1, 1, 2, 2, 3, 3, 4, 4, 4, 5` after the leading 0.
 * The combo counter is 0 for the first clear of a run, so the first clear adds
 * nothing.
 */
export const COMBO_BONUS: readonly number[] = [0, 0, 1, 1, 1, 2, 2, 3, 3, 4, 4, 4, 5];

/**
 * Chain attack, indexed by chain length, clamped at the last index.
 *
 * `docs/DESIGN.md`: `0, 0, 3, 7, 12, 20, 30, 42`. Index 1 is zero on purpose —
 * a clear with no follow-up collapse pays nothing, which is the whole point of
 * the chain mode. The clamp beyond index 7 is `docs/prompt-review.md` R5; see
 * `docs/decisions/0004-chain-table-clamp.md`.
 */
export const CHAIN_ATTACK: readonly number[] = [0, 0, 3, 7, 12, 20, 30, 42];

/** Added to a quad or T-spin that follows another quad or T-spin. */
export const BACK_TO_BACK_BONUS = 1;

/** Added when the board is left completely empty. */
export const PERFECT_CLEAR_BONUS = 10;

/** Highest chain index the table describes. */
export const MAX_CHAIN_INDEX = CHAIN_ATTACK.length - 1;

function lookup(table: readonly number[], index: number): number {
  const clamped = Math.min(Math.max(index, 0), table.length - 1);
  return table[clamped] ?? 0;
}

/** Base attack for a clear, before combo, back-to-back and perfect clear. */
export function baseAttack(lines: number, tSpin: boolean): number {
  return lookup(tSpin ? TSPIN_ATTACK : LINE_ATTACK, lines);
}

/** Combo bonus for a combo counter. Negative counters mean no combo. */
export function comboBonus(combo: number): number {
  return lookup(COMBO_BONUS, combo);
}

/** Chain attack for a chain length, clamped at the last table entry. */
export function chainAttack(chain: number): number {
  return lookup(CHAIN_ATTACK, chain);
}

/**
 * Whether a clear keeps a back-to-back run alive.
 *
 * `docs/DESIGN.md` names quads and T-spins. Everything else breaks the run.
 */
export function isBackToBackClear(lines: number, tSpin: boolean): boolean {
  return tSpin || lines === 4;
}
